// VIDE CONSTITUICAO_REGRAS.SH sec.2.5 | Art.182 (Split 4 Buckets) Art.181-I
// ============================================================================
// SplitEngine — Decomposicao logica de receita PJ em 4 buckets
// ----------------------------------------------------------------------------
// Implementa Arts. 181-182 da Constituicao RDA V24.0.
// Modulo PURO de calculo: nao executa PIX-out, nao move dinheiro,
// nao emite ordem bancaria. Apenas decompoe valores para visualizacao
// no RectaFinanceDashboard.jsx.
//
// Cláusula petrea (Art. 181-IV): este modulo JAMAIS deve ser usado para
// programar transferencia automatica do bucket3 (Reserva de Emergencia PJ)
// para qualquer conta de Pessoa Fisica.
// ============================================================================

// Aliquota DAS Simples Nacional default conservador.
// CNAE 58.21-2-00 (Edicao integrada a impressao de livros) tipicamente
// se enquadra no Anexo III faixa 1 (~6%) ou Anexo II.
// Calibrar com contador.
export const SIMPLES_NACIONAL_DEFAULT_ALIQUOTA = 0.06;

// Art. 181-I — minimo absoluto de retencao em Reserva de Emergencia PJ.
// Pode subir conforme caixa cresce; nunca descer abaixo deste valor.
export const RESERVA_PJ_MIN_PERCENT = 0.20;

// Identificadores estaveis dos buckets para uso em rotulos/render.
export const BUCKETS = Object.freeze({
  CUSTOS_DIRETOS: 'bucket1_custosDiretos',
  RESERVA_IMPOSTO: 'bucket2_reservaImposto',
  RESERVA_EMERGENCIA_PJ: 'bucket3_reservaEmergenciaPJ',
  CAIXA_LIVRE: 'bucket4_caixaLivre',
});

// Rotulos PUBLICOS — controlados por Art. 181-II (sigilo de nomenclatura).
// NUNCA alterar para expressoes que conectem o bucket3 a pessoa fisica
// do titular ("aposentadoria", "reserva pessoal" etc.).
export const BUCKET_LABELS = Object.freeze({
  [BUCKETS.CUSTOS_DIRETOS]: 'Custos Diretos',
  [BUCKETS.RESERVA_IMPOSTO]: 'Reserva de Imposto',
  [BUCKETS.RESERVA_EMERGENCIA_PJ]: 'Reserva de Emergência PJ',
  [BUCKETS.CAIXA_LIVRE]: 'Caixa Livre',
});

export const BUCKET_DESCRIPTIONS = Object.freeze({
  [BUCKETS.CUSTOS_DIRETOS]:
    'Custo da gráfica/fornecedor direto correspondente às vendas. Pago manualmente via app C6 PJ.',
  [BUCKETS.RESERVA_IMPOSTO]:
    'DAS Simples Nacional acumulado para pagamento até o dia 20 do mês seguinte.',
  [BUCKETS.RESERVA_EMERGENCIA_PJ]:
    'Lucros retidos para investimento em ativos PJ (CDB PJ, Tesouro PJ). Intocável por automação.',
  [BUCKETS.CAIXA_LIVRE]:
    'Saldo operacional após custos, imposto e reserva. Cobre contas PJ correntes e pró-labore de subsistência.',
});

const toNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Calcula a decomposicao em 4 buckets a partir de receitaBruta + custos.
 *
 * Formula:
 *   bucket1 (custosDiretos)  = max(0, custosDiretos)
 *   bucket2 (reservaImposto) = receitaBruta * aliquotaImposto
 *   lucroLiquido             = max(0, receitaBruta - bucket1 - bucket2)
 *   bucket3 (reservaPJ)      = lucroLiquido * percentReservaPJ
 *   bucket4 (caixaLivre)     = lucroLiquido - bucket3
 *
 * @param {object} input
 * @param {number} input.receitaBruta        — Faturamento bruto do periodo (R$)
 * @param {number} input.custosDiretos       — Custos diretos (graficas/fornecedores) (R$)
 * @param {number} [input.aliquotaImposto]   — Aliquota DAS efetiva (default 6%)
 * @param {number} [input.percentReservaPJ]  — Percentual reserva PJ (default 20%)
 * @returns {object} resultado com os 4 buckets + metadados
 */
export function computeSplit({
  receitaBruta = 0,
  custosDiretos = 0,
  aliquotaImposto = SIMPLES_NACIONAL_DEFAULT_ALIQUOTA,
  percentReservaPJ = RESERVA_PJ_MIN_PERCENT,
} = {}) {
  const receita = Math.max(0, toNumber(receitaBruta));
  const custos = Math.max(0, toNumber(custosDiretos));
  const aliq = Math.max(0, Math.min(1, toNumber(aliquotaImposto)));
  const pctPJ = Math.max(RESERVA_PJ_MIN_PERCENT, Math.min(1, toNumber(percentReservaPJ)));

  const bucket1 = custos;
  const bucket2 = receita * aliq;
  const lucroLiquido = Math.max(0, receita - bucket1 - bucket2);
  const bucket3 = lucroLiquido * pctPJ;
  const bucket4 = Math.max(0, lucroLiquido - bucket3);

  return {
    receitaBruta: receita,
    [BUCKETS.CUSTOS_DIRETOS]: bucket1,
    [BUCKETS.RESERVA_IMPOSTO]: bucket2,
    [BUCKETS.RESERVA_EMERGENCIA_PJ]: bucket3,
    [BUCKETS.CAIXA_LIVRE]: bucket4,
    lucroLiquido,
    config: { aliquotaImposto: aliq, percentReservaPJ: pctPJ },
  };
}

/**
 * Conveniencia: extrai entradas relevantes de um snapshot Bling e
 * delega para computeSplit. Sem dados granulares de custo-por-venda,
 * usa total_a_pagar PJ como proxy de custosDiretos do periodo.
 *
 * Esse proxy e provisorio — quando o backend tiver vinculo
 * proposta -> NF de compra, substituir por soma dos custos reais.
 *
 * @param {object} snapshot — payload de useRectaBlingSnapshot
 * @param {object} [overrides] — receitaBrutaOverride, custosDiretosOverride,
 *                                aliquotaImposto, percentReservaPJ
 */
export function computeSplitFromSnapshot(snapshot, overrides = {}) {
  const kpis = snapshot?.kpis || {};
  const receitaBruta = overrides.receitaBrutaOverride ?? kpis.faturamento_30d ?? 0;
  const custosDiretos = overrides.custosDiretosOverride ?? kpis.total_a_pagar ?? 0;
  return computeSplit({
    receitaBruta,
    custosDiretos,
    aliquotaImposto: overrides.aliquotaImposto,
    percentReservaPJ: overrides.percentReservaPJ,
  });
}

/**
 * Helper de formatacao em BRL.
 */
export const fmtBRL = (v) => {
  if (v == null || Number.isNaN(Number(v))) return 'R$ 0,00';
  return Number(v).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

/**
 * Helper de formatacao em percentual.
 */
export const fmtPct = (v, digits = 1) => {
  if (v == null || Number.isNaN(Number(v))) return '0%';
  return `${(Number(v) * 100).toFixed(digits)}%`;
};
